import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ArtistHeader = ({ artistData, completionPercentage, onAccountAction, saving }) => {
  const [showAccountActions, setShowAccountActions] = useState(false);

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800 border-green-200',
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      inactive: 'bg-gray-100 text-gray-800 border-gray-200',
      suspended: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors?.[status] || colors?.pending;
  };

  const getArtistTypeIcon = (type) => {
    const icons = {
      music: 'Music',
      visual: 'Palette',
      actor: 'Video',
      model: 'Camera'
    };
    return icons?.[type] || 'User';
  };

  const accountActions = [
    { 
      key: 'activate', 
      label: 'Activate Account', 
      icon: 'CheckCircle',
      color: 'text-green-600 hover:bg-green-50',
      disabled: artistData?.status === 'active'
    },
    { 
      key: 'suspend', 
      label: 'Suspend Account', 
      icon: 'XCircle',
      color: 'text-red-600 hover:bg-red-50',
      disabled: artistData?.status === 'suspended'
    },
    { 
      key: 'deactivate', 
      label: 'Deactivate Account', 
      icon: 'MinusCircle',
      color: 'text-yellow-600 hover:bg-yellow-50',
      disabled: artistData?.status === 'inactive'
    },
    { 
      key: 'reset-password', 
      label: 'Reset Password', 
      icon: 'RotateCcw',
      color: 'text-blue-600 hover:bg-blue-50',
      disabled: false
    }
  ];

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
        {/* Artist Info */}
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-xl font-bold text-primary-foreground">
              {artistData?.full_name?.charAt(0)?.toUpperCase() || 'A'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold text-text-primary truncate">
                {artistData?.full_name || 'Artist Name'}
              </h1>
              <div className={`px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(artistData?.status)}`}>
                {artistData?.status?.charAt(0)?.toUpperCase() + artistData?.status?.slice(1) || 'Pending'}
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm text-text-secondary mb-3">
              <div className="flex items-center gap-1">
                <Icon name="Mail" size={16} />
                <span>{artistData?.email || 'No email'}</span>
              </div>
              {artistData?.artist_type && (
                <>
                  <span className="hidden sm:inline">•</span>
                  <div className="flex items-center gap-1">
                    <Icon name={getArtistTypeIcon(artistData?.artist_type)} size={16} />
                    <span className="capitalize">{artistData?.artist_type}</span>
                  </div>
                </>
              )}
              {artistData?.location && (
                <>
                  <span className="hidden sm:inline">•</span>
                  <div className="flex items-center gap-1">
                    <Icon name="MapPin" size={16} />
                    <span>{artistData?.location}</span>
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center gap-2 text-sm text-text-secondary">
              <span>Profile Completion:</span>
              <div className="flex-1 max-w-xs bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${completionPercentage}%` }}
                ></div>
              </div>
              <span className="font-medium text-text-primary">{completionPercentage}%</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0">
          <Button
            variant="outline"
            onClick={() => window.open(`/mobile-app-preview?artistId=${artistData?.id}`, '_blank')}
            iconName="Smartphone"
            iconPosition="left"
            size="sm"
          >
            Preview Mobile
          </Button>
          
          <div className="relative">
            <Button
              variant="outline"
              onClick={() => setShowAccountActions(!showAccountActions)}
              iconName="Settings"
              iconPosition="left"
              size="sm"
              disabled={saving}
            >
              Account Actions
            </Button>
            
            {showAccountActions && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-card border border-border rounded-lg shadow-lg z-10">
                <div className="p-2">
                  <div className="text-sm font-medium text-text-primary mb-2 px-3 py-2">
                    Account Management
                  </div>
                  {accountActions?.map((action) => (
                    <button
                      key={action?.key}
                      onClick={() => {
                        onAccountAction?.(action?.key);
                        setShowAccountActions(false);
                      }}
                      disabled={action?.disabled || saving}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded transition-colors ${
                        action?.disabled 
                          ? 'text-text-secondary bg-gray-50 cursor-not-allowed' 
                          : action?.color
                      }`}
                    >
                      <Icon name={action?.icon} size={16} />
                      {action?.label}
                      {action?.disabled && (
                        <span className="ml-auto text-xs text-text-secondary">(Current)</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Click outside handler */}
      {showAccountActions && (
        <div 
          className="fixed inset-0 z-5" 
          onClick={() => setShowAccountActions(false)}
        />
      )}
    </div>
  );
};

export default ArtistHeader;